import React, { useCallback, useEffect, useState } from 'react';
import { Button } from '../../components/ui/Button';
import { TextField } from '../../components/forms/TextField';
import { ImageUpload, type PendingFile } from '../../components/forms/ImageUpload';
import { RichTextEditor } from '../../components/forms/RichTextEditor';
import { settingsApi, type AboutUsSection } from '../../api/SettingsApi';
import { filesApi } from '../../api/FilesApi';
import { cn } from '../../utils/cn';
import { Toggle, Toast, LoadingSpinner } from './SettingWidgets';
import { useI18n } from '../../i18n/I18nContext';

type SectionDraft = AboutUsSection & {
  pendingImage: PendingFile | null;
};

function emptySection(): SectionDraft {
  return { title: '', content: '', imageUrl: '', pendingImage: null };
}

export function AboutUsPage() {
  const { t } = useI18n();
  const [enabled, setEnabled] = useState(false);
  const [sections, setSections] = useState<SectionDraft[]>([emptySection()]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const showToast = useCallback((type: 'success' | 'error', message: string) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3000);
  }, []);

  useEffect(() => {
    settingsApi
      .get()
      .then(({ data }) => {
        setEnabled(data.aboutUsEnabled ?? false);
        const content = data.aboutUsContent;
        if (content && content.length > 0) {
          setSections(
            content.map((s) => ({
              title: s.title ?? '',
              content: s.content ?? '',
              imageUrl: s.imageUrl ?? '',
              imageId: s.imageId,
              pendingImage: null,
            }))
          );
        }
      })
      .catch(() => showToast('error', t('aboutUs.loadFailed')))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showToast]);

  const updateSection = (index: number, partial: Partial<SectionDraft>) => {
    setSections((prev) => prev.map((s, i) => (i === index ? { ...s, ...partial } : s)));
  };

  const removeSection = (index: number) => {
    setSections((prev) => prev.filter((_, i) => i !== index));
  };

  const addSection = () => {
    setSections((prev) => [...prev, emptySection()]);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const uploaded: AboutUsSection[] = [];
      for (const section of sections) {
        let imageUrl = section.imageUrl ?? '';
        let imageId = section.imageId;

        if (section.pendingImage) {
          const { data: file } = await filesApi.upload(
            section.pendingImage.name,
            section.pendingImage.base64
          );
          imageUrl = file.s3Url;
          imageId = file.fileId;
        }

        uploaded.push({
          title: section.title,
          content: section.content,
          imageUrl,
          imageId,
        });
      }

      await settingsApi.update({
        aboutUsEnabled: enabled,
        aboutUsContent: uploaded,
      });

      setSections(
        uploaded.map((s) => ({ ...s, pendingImage: null }))
      );
      showToast('success', t('aboutUs.saveSuccess'));
    } catch {
      showToast('error', t('aboutUs.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="mx-auto max-w-4xl">
      <Toast toast={toast} />

      {/* Header */}
      <div className="flex flex-col gap-3 pb-6 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100">{t('aboutUs.title')}</h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            {t('aboutUs.subtitle')}
          </p>
        </div>
        <Button className="w-full sm:w-auto" onClick={handleSave} disabled={saving}>
          {saving ? t('common.saving') : t('common.saveChanges')}
        </Button>
      </div>

      {/* Enable toggle */}
      <div className="rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900">
        <div className="px-4 py-5 sm:px-6">
          <Toggle
            label={t('aboutUs.enableToggle')}
            description={t('aboutUs.enableToggleHint')}
            checked={enabled}
            onChange={setEnabled}
          />
        </div>
      </div>

      {/* Content sections */}
      {sections.map((section, index) => (
        <div
          key={index}
          className="mt-8 rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900"
        >
          <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3 sm:px-6 dark:border-slate-800">
            <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300">
              {t('aboutUs.sectionNumber', { n: index + 1 })}
            </h3>
            {sections.length > 1 && (
              <button
                type="button"
                onClick={() => removeSection(index)}
                className="rounded-md px-2.5 py-1.5 text-xs font-medium text-rose-600 hover:bg-rose-50 transition dark:text-rose-400 dark:hover:bg-rose-950/40"
              >
                {t('aboutUs.removeSection')}
              </button>
            )}
          </div>

          <div className="space-y-5 px-4 py-5 sm:px-6">
            <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
              {/* Image */}
              <ImageUpload
                label={t('aboutUs.sectionImage')}
                currentUrl={section.imageUrl}
                pendingFile={section.pendingImage}
                onSelect={(file) => updateSection(index, { pendingImage: file })}
                onClear={() => updateSection(index, { imageUrl: '', imageId: undefined })}
              />

              {/* Title */}
              <div className="flex flex-col justify-end">
                <TextField
                  label={t('aboutUs.sectionTitle')}
                  placeholder={t('aboutUs.sectionTitlePlaceholder')}
                  value={section.title}
                  onChange={(e) =>
                    updateSection(index, { title: (e.target as HTMLInputElement).value })
                  }
                />
              </div>
            </div>

            {/* Rich text content */}
            <RichTextEditor
              label={t('aboutUs.sectionContentLabel')}
              placeholder={t('aboutUs.sectionContentPlaceholder')}
              value={section.content}
              onChange={(v) => updateSection(index, { content: v })}
            />
          </div>
        </div>
      ))}

      {/* Add section */}
      <div className="pt-4">
        <button
          type="button"
          onClick={addSection}
          className={cn(
            'w-full rounded-xl border-2 border-dashed border-slate-300 py-4 text-sm font-medium text-slate-500 transition dark:border-slate-600 dark:text-slate-400',
            'hover:border-red-300 hover:text-red-600 hover:bg-red-50/50 dark:hover:border-red-700 dark:hover:text-red-400 dark:hover:bg-red-950/20'
          )}
        >
          {t('aboutUs.addSection')}
        </button>
      </div>

      {/* Footer */}
      <div className="flex flex-col-reverse gap-3 pb-8 pt-6 sm:flex-row sm:items-center sm:justify-end">
        <Button variant="secondary" onClick={() => window.location.reload()}>
          {t('common.discardChanges')}
        </Button>
        <Button onClick={handleSave} disabled={saving}>
          {saving ? t('common.saving') : t('common.saveChanges')}
        </Button>
      </div>
    </div>
  );
}
